import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import "./GpsTrackingMap.css";
import { toast } from "react-toastify";
import Table from "../../../Components/UiComponents/Table/Table";
import { t } from "i18next";
import {
  MapPin, ChevronDown, X, Check, Calendar, Car,
  AlertCircle, Power, Play, Pause,
  Search, RefreshCw, Navigation, Clock
} from "lucide-react";
import { postDataWithBody, getData } from "../../../Services/Services";
import { getlivestatus, getVehicleDataForTracking, getvehicles, getAllVehicles, getVehicleByNumber } from "../../../Services/EndPoints";
import { GoogleMap, useJsApiLoader, Marker, Circle, InfoWindow, TransitLayer } from "@react-google-maps/api";

// ─── Shared loader config (same ID + libraries as TripAction) ─────────────────
import {
  GOOGLE_MAPS_API_KEY,
  GOOGLE_MAPS_LIBRARIES,
  GOOGLE_MAPS_LOADER_ID,
} from "../../../../src/Pages/DMS/GpsTrackManagement/Googlemapsloader";

const MAP_CONTAINER_STYLE = { height: "100%", width: "100%" };
const DEFAULT_CENTER = { lat: 17.4515, lng: 78.3690 };

// Vehicle images/videos share a single `image_paths` list on the API — the only
// way to tell them apart is the file extension on the URL.
const VIDEO_EXTENSIONS = ["mp4", "mov", "avi", "webm", "mkv", "m4v", "3gp"];
const guessMediaTypeFromUrl = (url) => {
  const ext = (url.split(".").pop() || "").split(/[?#]/)[0].toLowerCase();
  return VIDEO_EXTENSIONS.includes(ext) ? "video" : "image";
};

// ─── Status helpers ──────────────────────────────────────────────────────────

const STATUS_COLORS = {
  MOVING: "#28a745",
  IDLE: "#ffc107",
  OFFLINE: "#6c757d",
  MAINTENANCE: "#17a2b8",
};

const getStatusZIndex = (status) =>
  ({ MOVING: 1000, IDLE: 500, MAINTENANCE: 400, OFFLINE: 100 }[status] || 100);

const getStatusBadgeStyle = (status) =>
  ({
    MOVING: { background: "#28a745", color: "white" },
    IDLE: { background: "#ffc107", color: "white" },
    OFFLINE: { background: "#6c757d", color: "white" },
    MAINTENANCE: { background: "#17a2b8", color: "white" },
  }[status] || { background: "#6c757d", color: "white" });

const getStatusIcon = (status) => {
  switch (status) {
    case "MOVING": return <Play size={14} />;
    case "IDLE": return <Pause size={14} />;
    case "OFFLINE": return <Power size={14} />;
    case "MAINTENANCE": return <AlertCircle size={14} />;
    default: return <Car size={14} />;
  }
};

// ─── Vehicle emoji map ────────────────────────────────────────────────────────
const VEHICLE_EMOJI_MAP = {
  car: '🚗', sedan: '🚗', bus: '🚌', minibus: '🚌',
  truck: '🚛', lorry: '🚛', bike: '🏍️', bicycle: '🚲',
  ambulance: '🚑', firetruck: '🚒', police: '🚓', van: '🚐',
  taxi: '🚕', tractor: '🚜',
};
const getVehicleEmoji = (type) =>
  VEHICLE_EMOJI_MAP[(type || '').toLowerCase().replace(/[\s_-]+/g, '')] || '🚘';

// Build SVG data URL marker for Google Maps — emoji icon with status-color ring
const buildMarkerIcon = (status, vehicleNumber, eventType, vehicleType) => {
  const color = STATUS_COLORS[status] || "#6c757d";
  const emoji = getVehicleEmoji(vehicleType);
  const hasEvent =
    eventType &&
    eventType.toUpperCase() !== "STABLE" &&
    eventType.toUpperCase() !== "NORMAL";

  const charWidth = 7.5;
  const padding = 14;
  const labelW = Math.max(60, vehicleNumber.length * charWidth + padding);
  const totalW = labelW + (hasEvent ? 16 : 0);
  const cx = labelW / 2;

  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${totalW}" height="62" viewBox="0 0 ${totalW} 62">
      <defs>
        <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="1" stdDeviation="1.5" flood-opacity="0.25"/>
        </filter>
      </defs>
      <rect x="1" y="1" width="${labelW - 2}" height="18" rx="4"
            fill="white" stroke="${color}" stroke-width="1.5" filter="url(#shadow)"/>
      <text x="${cx}" y="13.5" text-anchor="middle"
            font-size="9.5" font-weight="700"
            font-family="Arial,sans-serif" fill="#222"
            letter-spacing="0.3">${vehicleNumber}</text>
      <line x1="${cx}" y1="19" x2="${cx}" y2="30"
            stroke="${color}" stroke-width="1.5" stroke-dasharray="2,1"/>
      <circle cx="${cx}" cy="45" r="14" fill="${color}" stroke="white" stroke-width="2.5" filter="url(#shadow)"/>
      <text x="${cx}" y="50" text-anchor="middle" font-size="14">${emoji}</text>
      ${hasEvent
        ? `<circle cx="${labelW + 6}" cy="8" r="8" fill="#dc3545" stroke="white" stroke-width="1.5"/>
           <text x="${labelW + 6}" y="12" text-anchor="middle" font-size="10" font-weight="bold" fill="white">!</text>`
        : ""}
    </svg>`;

  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: { width: totalW, height: 62 },
    anchor: { x: Math.round(cx), y: 59 },
  };
};

// ─── Offset overlapping markers ──────────────────────────────────────────────

const offsetOverlappingMarkers = (vehicles) => {
  const OFFSET = 0.0003;
  const groups = {};

  vehicles.forEach((v) => {
    const lat = parseFloat(v.latitude);
    const lng = parseFloat(v.longitude);
    if (isNaN(lat) || isNaN(lng)) return;
    const key = `${lat.toFixed(6)}_${lng.toFixed(6)}`;
    if (!groups[key]) groups[key] = [];
    groups[key].push({ ...v, originalLat: lat, originalLng: lng });
  });

  const result = [];
  Object.values(groups).forEach((group) => {
    if (group.length === 1) {
      result.push({ ...group[0], latitude: group[0].originalLat, longitude: group[0].originalLng });
    } else {
      group.forEach((v, i) => {
        const angle = (2 * Math.PI * i) / group.length;
        result.push({
          ...v,
          latitude: v.originalLat + OFFSET * Math.cos(angle),
          longitude: v.originalLng + OFFSET * Math.sin(angle),
          isOffset: true,
          groupSize: group.length,
        });
      });
    }
  });
  return result;
};

// ─── Map layer options — inline SVGs (no lucide dependency) ──────────────────

const MAP_TYPE_OPTIONS = [
  {
    id: "roadmap", label: "Standard",
    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21"/><line x1="9" y1="3" x2="9" y2="18"/><line x1="15" y1="6" x2="15" y2="21"/></svg>,
  },
  {
    id: "satellite", label: "Satellite",
    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>,
  },
  {
    id: "terrain", label: "Terrain",
    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="3 18 9 6 13 13 16 9 21 18"/></svg>,
  },
  {
    id: "hybrid", label: "Hybrid",
    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/><line x1="2" y1="12" x2="22" y2="12"/></svg>,
  },
];

// ─── Shared map control button style ─────────────────────────────────────────
const mapBtnStyle = (active) => ({
  width: 40, height: 40, border: "none",
  background: active ? "#1a7a73" : "white",
  borderRadius: 8, cursor: "pointer", display: "flex",
  alignItems: "center", justifyContent: "center",
  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
  flexShrink: 0,
});

// ─── Layer Control ────────────────────────────────────────────────────────────
const CustomLayerControl = ({ mapType, setMapType }) => {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ position: "relative" }}>
      <button onClick={() => setOpen((p) => !p)} style={mapBtnStyle(false)} title="Map layers">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polygon points="12 2 2 7 12 12 22 7 12 2"/>
          <polyline points="2 17 12 22 22 17"/>
          <polyline points="2 12 12 17 22 12"/>
        </svg>
      </button>
      {open && (
        <div style={{
          position: "absolute", top: 0, right: 45,
          background: "white", borderRadius: 10, padding: 8,
          boxShadow: "0 4px 16px rgba(0,0,0,0.14)", minWidth: 160,
          border: "1px solid #e2e8f0",
        }}>
          {MAP_TYPE_OPTIONS.map((opt) => (
            <div
              key={opt.id}
              onClick={() => { setMapType(opt.id); setOpen(false); }}
              style={{
                display: "flex", alignItems: "center", gap: 9,
                padding: "8px 10px", cursor: "pointer", borderRadius: 7,
                background: mapType === opt.id ? "#f0fdfb" : "transparent",
                transition: "background 0.15s",
              }}
            >
              <span style={{ color: mapType === opt.id ? "#1a7a73" : "#94a3b8" }}>{opt.icon}</span>
              <span style={{ fontSize: 13, fontWeight: mapType === opt.id ? 600 : 400, color: mapType === opt.id ? "#1a7a73" : "#334155" }}>{opt.label}</span>
              {mapType === opt.id && (
                <span style={{ marginLeft: "auto", width: 7, height: 7, borderRadius: "50%", background: "#1a7a73" }} />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Traffic toggle ───────────────────────────────────────────────────────────
const TrafficToggleControl = ({ showTraffic, setShowTraffic }) => (
  <button onClick={() => setShowTraffic((p) => !p)} title={showTraffic ? "Hide traffic" : "Show traffic"} style={mapBtnStyle(showTraffic)}>
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={showTraffic ? "white" : "#333"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="7" y="2" width="10" height="20" rx="3"/>
      <circle cx="12" cy="7" r="1.4" fill="#ef4444" stroke="none"/>
      <circle cx="12" cy="12" r="1.4" fill="#f59e0b" stroke="none"/>
      <circle cx="12" cy="17" r="1.4" fill="#22c55e" stroke="none"/>
      <line x1="12" y1="22" x2="12" y2="20"/>
    </svg>
  </button>
);

// ─── Popup content rendered inside a native InfoWindow at marker lat/lng ─────
// Using InfoWindow (not a custom OverlayView) so Google's own viewport-aware
// auto-pan brings the popup fully into view — matches Planned Route / TripAction.

const VehiclePopupContent = ({ vehicle, address, onClose, eventType, formatDateDisplay, vehicleInfo }) => {
  const statusStyle = getStatusBadgeStyle(vehicle.status);
  const hasEvent = eventType && !["STABLE", "NORMAL", "HALT"].includes(eventType.toUpperCase());
  const emoji = getVehicleEmoji(vehicleInfo?.vehicle_type || vehicleInfo?.vehicleType);

  const Row = ({ label, value }) => (
    <div style={{ display: "grid", gridTemplateColumns: "90px 1fr", gap: 4, padding: "4px 0", borderBottom: "1px solid #f1f5f9" }}>
      <span style={{ color: "#94a3b8", fontSize: 11, fontWeight: 600 }}>{label}</span>
      <span style={{ color: "#1e293b", fontSize: 11, textAlign: "right", wordBreak: "break-word" }}>{value}</span>
    </div>
  );

  const SectionHead = ({ children, first }) => (
    <div style={{ fontSize: 9, fontWeight: 700, color: "#fff", background: "linear-gradient(90deg,#1a7a73,#1a7a73)", padding: "2px 10px", margin: `${first ? 0 : 8}px -12px 4px`, textTransform: "uppercase", letterSpacing: "0.6px" }}>{children}</div>
  );

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        width: 272,
        background: "#fff",
        borderRadius: 12,
        boxShadow: "0 6px 28px rgba(0,0,0,0.28)",
        overflow: "hidden",
        fontFamily: "'Segoe UI',sans-serif",
        animation: "fadeInPanel 0.15s ease",
        pointerEvents: "auto",
        position: "relative",
      }}
    >
      {/* Tail */}
      <div style={{
        position: "absolute", bottom: -10, left: "50%",
        transform: "translateX(-50%)",
        width: 0, height: 0,
        borderLeft: "10px solid transparent",
        borderRight: "10px solid transparent",
        borderTop: `10px solid ${statusStyle.background}`,
      }} />

      {/* Header */}
      <div style={{
        background: statusStyle.background, color: "white",
        padding: "9px 12px",
        display: "flex", alignItems: "center", gap: 8,
      }}>
        <span style={{ fontSize: 22, lineHeight: 1, flexShrink: 0 }}>{emoji}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 800, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{vehicle.vehicleNumber}</div>
          {vehicleInfo?.vehicle_type && <div style={{ fontSize: 10, opacity: 0.85, marginTop: 1 }}>{vehicleInfo.vehicle_type}</div>}
        </div>
        <span style={{ background: "rgba(255,255,255,0.28)", padding: "1px 7px", borderRadius: 20, fontSize: 10, fontWeight: 700, flexShrink: 0 }}>
          {vehicle.status}
        </span>
        <button
          onClick={(e) => { e.stopPropagation(); onClose(); }}
          style={{ background: "rgba(255,255,255,0.25)", border: "none", color: "white", borderRadius: "50%", width: 22, height: 22, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: "bold", lineHeight: 1, flexShrink: 0 }}
        >✕</button>
      </div>

      {/* Body */}
      <div style={{ padding: "0 12px 10px", maxHeight: 340, overflowY: "auto" }}>
        {hasEvent && (
          <div style={{ marginTop: 8, marginBottom: 6, padding: "4px 8px", background: "#fff3cd", border: "1px solid #ffc107", borderRadius: 6, display: "flex", alignItems: "center", gap: 6 }}>
            <AlertCircle size={12} color="#856404" />
            <span style={{ fontSize: 10, color: "#856404", fontWeight: 600 }}>{eventType.replace(/_/g, " ")}</span>
          </div>
        )}

        {/* Live Status */}
        <SectionHead first={!hasEvent}>Live Status</SectionHead>
        <Row label="Speed" value={`${vehicle.speed ?? 0} km/h`} />
        <Row label="Last Updated" value={formatDateDisplay(vehicle.vehicleTrackingTime)} />

        {/* Location */}
        <SectionHead>Location</SectionHead>
        <div style={{ padding: "4px 0", fontSize: 11, color: address ? "#334155" : "#94a3b8", fontStyle: address ? "normal" : "italic", lineHeight: 1.5 }}>
          📍 {address || "Fetching address…"}
        </div>

        {vehicle.isOffset && vehicle.groupSize > 1 && (
          <div style={{ fontSize: 10, color: "#6c757d", fontStyle: "italic", marginTop: 6 }}>
            {vehicle.groupSize} vehicles at this location
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Live Google Map ─────────────────────────────────────────────────────────

const LiveGoogleMap = ({
  vehicles, selectedVehicle, mapCenter, vehicleAddresses,
  mapType, setMapType, formatDateDisplay, getMapRef,
  animatedPositions, vehicleInfoCache, allVehiclesMap, onMarkerClick,
  showTraffic, setShowTraffic,
}) => {
  const [activePopupVehicle, setActivePopupVehicle] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const [showTransit, setShowTransit] = useState(false);
  const mapRef = useRef(null);
  const trafficLayerRef = useRef(null);
  const containerRef = useRef(null);
  const shareRef = useRef(null);

  useEffect(() => {
    if (selectedVehicle) setActivePopupVehicle(selectedVehicle);
  }, [selectedVehicle]);

  // Fullscreen toggle
  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  }, []);

  useEffect(() => {
    const onFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFsChange);
    return () => document.removeEventListener("fullscreenchange", onFsChange);
  }, []);

  // Close share menu on outside click
  useEffect(() => {
    const handler = (e) => { if (shareRef.current && !shareRef.current.contains(e.target)) setShowShareMenu(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Build Google Maps share link for active/selected vehicle
  const getShareLink = useCallback(() => {
    const v = activePopupVehicle || selectedVehicle;
    if (v) {
      const pos = animatedPositions?.[v.vehicleNumber] || { lat: parseFloat(v.latitude), lng: parseFloat(v.longitude) };
      if (!isNaN(pos.lat) && !isNaN(pos.lng)) {
        return `https://www.google.com/maps?q=${pos.lat},${pos.lng}`;
      }
    }
    if (mapRef.current) {
      const c = mapRef.current.getCenter();
      return `https://www.google.com/maps?q=${c.lat()},${c.lng()}`;
    }
    return "https://www.google.com/maps";
  }, [activePopupVehicle, selectedVehicle, animatedPositions]);

  const handleCopyLink = useCallback(() => {
    const link = getShareLink();
    navigator.clipboard.writeText(link).then(() => {
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2000);
    });
  }, [getShareLink]);

  const getWhatsAppMessage = useCallback(() => {
    const v = activePopupVehicle || selectedVehicle;
    const info = vehicleInfoCache?.[v?.vehicleNumber] || {};
    const link = getShareLink();
    const lines = [];
    lines.push('🚛 *Vehicle Location Update*');
    lines.push('');
    lines.push('*🔢 Vehicle Details*');
    if (v?.vehicleNumber) lines.push(`*Vehicle No:* ${v.vehicleNumber}`);
    if (info.vehicle_type || v?.vehicle_type) lines.push(`*Type:* ${info.vehicle_type || v?.vehicle_type}`);
    if (info.brand) lines.push(`*Brand:* ${info.brand}`);
    if (info.model) lines.push(`*Model:* ${info.model}`);
    if (info.fuel_type) lines.push(`*Fuel:* ${info.fuel_type}`);
    if (info.rc_number) lines.push(`*RC No:* ${info.rc_number}`);
    if (v?.status) lines.push(`*Status:* ${v.status}`);
    if (v?.speed != null) lines.push(`*Speed:* ${v.speed} km/h`);
    if (info.driver_name || info.driver_phone) {
      lines.push('');
      lines.push('*👤 Driver Details*');
      if (info.driver_name) lines.push(`*Name:* ${info.driver_name}`);
      if (info.driver_phone) lines.push(`*Phone:* ${info.driver_phone}`);
      if (info.license_number) lines.push(`*License:* ${info.license_number}`);
    }
    lines.push('');
    lines.push('*📍 Live Location*');
    lines.push(link);
    return lines.join('\n');
  }, [activePopupVehicle, selectedVehicle, vehicleInfoCache, getShareLink]);

  // Places zoom: zoom out 2 levels centered on active vehicle to reveal nearby POIs
  const handlePlacesZoom = useCallback(() => {
    if (!mapRef.current) return;
    if (activePopupVehicle) {
      const lat = parseFloat(activePopupVehicle.latitude);
      const lng = parseFloat(activePopupVehicle.longitude);
      if (!isNaN(lat) && !isNaN(lng)) mapRef.current.panTo({ lat, lng });
    }
    const z = mapRef.current.getZoom() ?? 15;
    mapRef.current.setZoom(Math.max(z - 2, 13));
  }, [activePopupVehicle]);

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
    map.addListener("click", () => setActivePopupVehicle(null));
    getMapRef?.(map);
    // Imperatively create traffic layer so it persists across map type changes
    if (window.google?.maps?.TrafficLayer && !trafficLayerRef.current) {
      trafficLayerRef.current = new window.google.maps.TrafficLayer();
    }
  }, [getMapRef]);

  // Re-attach traffic layer whenever showTraffic or mapType changes (fixes satellite)
  useEffect(() => {
    if (!mapRef.current || !window.google?.maps?.TrafficLayer) return;
    if (!trafficLayerRef.current) {
      trafficLayerRef.current = new window.google.maps.TrafficLayer();
    }
    if (showTraffic) {
      if (mapType === 'satellite') { setMapType('hybrid'); return; }
      trafficLayerRef.current.setMap(mapRef.current);
    } else {
      trafficLayerRef.current.setMap(null);
    }
  }, [showTraffic, mapType, setMapType]);

  // Auto-fit to all vehicles only once (on initial load) — live polling refreshes
  // `vehicles` every few seconds and must not keep resetting the user's manual zoom/pan.
  const hasFitBoundsRef = useRef(false);

  useEffect(() => {
    if (!mapRef.current || mapCenter || hasFitBoundsRef.current) return;
    const validCoords = vehicles
      .map((v) => {
        const lat = parseFloat(v.latitude);
        const lng = parseFloat(v.longitude);
        return !isNaN(lat) && !isNaN(lng) ? { lat, lng } : null;
      })
      .filter(Boolean);
    if (validCoords.length === 0) return;
    hasFitBoundsRef.current = true;
    if (validCoords.length === 1) {
      mapRef.current.setCenter(validCoords[0]);
      mapRef.current.setZoom(15);
    } else {
      const bounds = new window.google.maps.LatLngBounds();
      validCoords.forEach((c) => bounds.extend(c));
      mapRef.current.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 });
    }
  }, [vehicles, mapCenter]);

  useEffect(() => {
    if (mapRef.current && mapCenter) {
      mapRef.current.panTo({ lat: mapCenter[0], lng: mapCenter[1] });
      mapRef.current.setZoom(15);
    }
  }, [mapCenter]);

  const vehiclesWithOffset = offsetOverlappingMarkers(vehicles);

  return (
    <div ref={containerRef} style={{ position: "relative", height: "100%", width: "100%" }}>
      <GoogleMap
        mapContainerStyle={MAP_CONTAINER_STYLE}
        center={DEFAULT_CENTER}
        zoom={6}
        mapTypeId={mapType}
        onLoad={onMapLoad}
        options={{
          mapTypeControl: false,
          streetViewControl: true,
          fullscreenControl: false,
          zoomControl: true,
          clickableIcons: false,
          gestureHandling: "greedy",
        }}
      >

        {vehiclesWithOffset.map((vehicle, index) => {
          // Use smoothly animated position if available, else fall back to raw
          const animPos = animatedPositions?.[vehicle.vehicleNumber];
          const lat = animPos ? animPos.lat : parseFloat(vehicle.latitude);
          const lng = animPos ? animPos.lng : parseFloat(vehicle.longitude);
          if (isNaN(lat) || isNaN(lng)) return null;
          const eventType = vehicle.eventType || vehicle.alertType || "STABLE";
          const isActive = activePopupVehicle?.vehicleNumber === vehicle.vehicleNumber;
          const vehicleType = allVehiclesMap?.[vehicle.vehicleNumber]?.vehicleType || '';

          return (
            <React.Fragment key={`${vehicle.vehicleNumber}-${index}`}>
              <Marker
                position={{ lat, lng }}
                icon={buildMarkerIcon(vehicle.status, vehicle.vehicleNumber, eventType, vehicleType)}
                zIndex={getStatusZIndex(vehicle.status)}
                onClick={() => {
                  if (isActive) { setActivePopupVehicle(null); }
                  else { setActivePopupVehicle(vehicle); onMarkerClick?.(vehicle); }
                }}
              />

              {isActive && (
                <Circle
                  center={{ lat, lng }}
                  radius={150}
                  options={{
                    strokeColor: STATUS_COLORS[vehicle.status] || "#6c757d",
                    strokeOpacity: 0.6, strokeWeight: 2,
                    fillColor: STATUS_COLORS[vehicle.status] || "#6c757d",
                    fillOpacity: 0.08,
                  }}
                />
              )}

              {isActive && (
                <InfoWindow
                  position={{ lat, lng }}
                  onCloseClick={() => setActivePopupVehicle(null)}
                  options={{ pixelOffset: new window.google.maps.Size(0, -72) }}
                >
                  <VehiclePopupContent
                    vehicle={vehicle}
                    address={vehicleAddresses[vehicle.vehicleNumber]}
                    eventType={eventType}
                    onClose={() => setActivePopupVehicle(null)}
                    formatDateDisplay={formatDateDisplay}
                    vehicleInfo={vehicleInfoCache?.[vehicle.vehicleNumber]}
                  />
                </InfoWindow>
              )}
            </React.Fragment>
          );
        })}
        {showTransit && <TransitLayer />}
      </GoogleMap>

      {/* Right-side control column: Fullscreen → Layers → Traffic → Places */}
      <div style={{
        position: "absolute", top: 10, right: 10, zIndex: 1000,
        display: "flex", flexDirection: "column", gap: 8,
      }}>
        {/* Fullscreen */}
        <button onClick={toggleFullscreen} title={isFullscreen ? "Exit fullscreen" : "Full screen"} style={mapBtnStyle(isFullscreen)}>
          {isFullscreen ? (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={isFullscreen ? "white" : "#333"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M8 3v3a2 2 0 0 1-2 2H3"/><path d="M21 8h-3a2 2 0 0 1-2-2V3"/>
              <path d="M3 16h3a2 2 0 0 1 2 2v3"/><path d="M16 21v-3a2 2 0 0 1 2-2h3"/>
            </svg>
          ) : (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M8 3H5a2 2 0 0 0-2 2v3"/><path d="M21 8V5a2 2 0 0 0-2-2h-3"/>
              <path d="M3 16v3a2 2 0 0 0 2 2h3"/><path d="M16 21h3a2 2 0 0 0 2-2v-3"/>
            </svg>
          )}
        </button>

        {/* Map layers */}
        <CustomLayerControl mapType={mapType} setMapType={setMapType} />

        {/* Traffic toggle */}
        <TrafficToggleControl showTraffic={showTraffic} setShowTraffic={setShowTraffic} />

        {/* Transit toggle */}
        <button onClick={() => setShowTransit(p => !p)} title={showTransit ? "Hide transit" : "Show transit"} style={mapBtnStyle(showTransit)}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
            stroke={showTransit ? "white" : "#333"} strokeWidth="2"
            strokeLinecap="round" strokeLinejoin="round">
            <rect x="4" y="2" width="16" height="16" rx="2"/>
            <path d="M4 10h16"/><path d="M8 2v8"/><path d="M16 2v8"/>
            <path d="M8 18l-2 4"/><path d="M16 18l2 4"/>
          </svg>
        </button>

        {/* Nearby places zoom */}
        <button onClick={handlePlacesZoom} title="Show nearby places" style={mapBtnStyle(false)}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="5"/>
            <path d="M11 6a5 5 0 0 1 0 10"/>
            <circle cx="11" cy="11" r="1" fill="#333" stroke="none"/>
            <line x1="16.5" y1="16.5" x2="21" y2="21"/>
            <circle cx="11" cy="4" r="1" fill="#333" stroke="none"/>
            <circle cx="18" cy="11" r="1" fill="#333" stroke="none"/>
            <circle cx="11" cy="18" r="1" fill="#333" stroke="none"/>
            <circle cx="4" cy="11" r="1" fill="#333" stroke="none"/>
          </svg>
        </button>

        {/* Share location — only visible when a vehicle is selected */}
        {(selectedVehicle || activePopupVehicle) && (
        <div ref={shareRef} style={{ position: 'relative' }}>
          <button
            onClick={() => setShowShareMenu(p => !p)}
            title="Share vehicle location"
            style={mapBtnStyle(showShareMenu)}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={showShareMenu ? "white" : "#333"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="18" cy="5" r="3"/>
              <circle cx="6" cy="12" r="3"/>
              <circle cx="18" cy="19" r="3"/>
              <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
              <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
            </svg>
          </button>
          {showShareMenu && (
            <div style={{ position: 'absolute', top: 0, right: 46, background: '#fff', borderRadius: 10, boxShadow: '0 4px 18px rgba(0,0,0,0.18)', border: '1px solid #e2e8f0', padding: '8px 6px', width: 190, zIndex: 100 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 6, padding: '0 6px' }}>Share Location</div>
              {/* Copy Link */}
              <button
                onClick={handleCopyLink}
                style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', background: shareCopied ? '#f0fdfb' : 'transparent', border: 'none', borderRadius: 7, padding: '7px 10px', cursor: 'pointer', fontSize: 13, color: shareCopied ? '#1a7a73' : '#1e293b', fontWeight: shareCopied ? 700 : 400, transition: 'all 0.18s' }}
              >
                {shareCopied ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#1a7a73" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#475569" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                )}
                {shareCopied ? 'Link Copied!' : 'Copy Link'}
              </button>
              {/* WhatsApp */}
              <a
                href={`https://wa.me/?text=${encodeURIComponent(getWhatsAppMessage())}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', background: 'transparent', border: 'none', borderRadius: 7, padding: '7px 10px', cursor: 'pointer', fontSize: 13, color: '#1e293b', textDecoration: 'none', transition: 'background 0.18s' }}
                onMouseEnter={e => e.currentTarget.style.background = '#f0fdf4'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="#25D366">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/>
                </svg>
                Share on WhatsApp
              </a>
            </div>
          )}
        </div>
        )}
      </div>
    </div>
  );
};

// ─── Multi-Select Dropdown ───────────────────────────────────────────────────

const MultiSelectDropdown = ({ options, selectedValues, onChange, placeholder, error }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredOptions = options.filter((o) =>
    o.value.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const isAllSelected =
    filteredOptions.length > 0 &&
    filteredOptions.every((o) => selectedValues.includes(o.value));

  const handleOptionToggle = (v) =>
    onChange(
      selectedValues.includes(v)
        ? selectedValues.filter((x) => x !== v)
        : [...selectedValues, v]
    );

  const handleSelectAll = () => {
    if (isAllSelected) {
      onChange(selectedValues.filter((v) => !filteredOptions.some((o) => o.value === v)));
    } else {
      onChange([...new Set([...selectedValues, ...filteredOptions.map((o) => o.value)])]);
    }
  };

  return (
    <div className="multi-select-container" ref={dropdownRef}>
      <div
        className={`multi-select-trigger ${error ? "is-invalid" : ""} ${isOpen ? "open" : ""}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="selected-tags-inline">
          {selectedValues.length === 0 ? (
            <span className="placeholder-text">{placeholder}</span>
          ) : (
            selectedValues.map((value, i) => (
              <span key={i} className="tag">
                {value}
                <X size={12} className="tag-remove" onClick={(e) => { e.stopPropagation(); handleOptionToggle(value); }} />
              </span>
            ))
          )}
        </div>
        <ChevronDown className={`chevron-icon ${isOpen ? "rotated" : ""}`} size={16} />
      </div>

      {isOpen && (
        <div className="multi-select-dropdown">
          <div className="search-container">
            <input
              type="text" placeholder="Search vehicles..." value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input" onClick={(e) => e.stopPropagation()}
            />
          </div>
          <div className="select-option select-all" onClick={handleSelectAll}>
            <div className="checkbox-container">
              <div className={`custom-checkbox ${isAllSelected ? "checked" : ""}`}>
                {isAllSelected && <Check size={12} />}
              </div>
            </div>
            <span className="option-text">{isAllSelected ? "Deselect All" : "Select All"}</span>
          </div>
          <div className="divider"></div>
          <div className="options-container">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <div key={option.id} className="select-option" onClick={() => handleOptionToggle(option.value)}>
                  <div className="checkbox-container">
                    <div className={`custom-checkbox ${selectedValues.includes(option.value) ? "checked" : ""}`}>
                      {selectedValues.includes(option.value) && <Check size={12} />}
                    </div>
                  </div>
                  <span className="option-text">{option.value}</span>
                </div>
              ))
            ) : (
              <div className="no-options">No vehicles found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const GpsTrackingMap = () => {
  const today = new Date().toISOString().split("T")[0];
  const [activeTab, setActiveTab] = useState("map");
  const [formData, setFormData] = useState({ startDate: today, endDate: today, vehicleType: [] });
  const [validationErrors, setValidationErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [vehicleTrackingData, setVehicleTrackingData] = useState([]);
  const [mapType, setMapType] = useState("roadmap");
  const [showTraffic, setShowTraffic] = useState(false);
  const itemsPerPage = 10;
  const [rowCount, setRowCount] = useState(0);
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState("");
  const [vehicleTypeList, setvehicleTypeList] = useState([]);
  const [allVehiclesMap,  setAllVehiclesMap]  = useState({});

  const [liveVehicles, setLiveVehicles] = useState([]);
  const [isLoadingLive, setIsLoadingLive] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [mapCenter, setMapCenter] = useState(null);
  const [liveSearchText, setLiveSearchText] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedVehicleNumber, setSelectedVehicleNumber] = useState("all");
  const [selectedDistrict, setSelectedDistrict] = useState("all");
  const [vehicleAddresses, setVehicleAddresses] = useState({});
  const [isLoadingAddresses, setIsLoadingAddresses] = useState(false);
  const [vehicleDetailsOpen, setVehicleDetailsOpen] = useState(false);
  const [vehicleInfoPanelOpen, setVehicleInfoPanelOpen] = useState(true);
  const [expandedVehicleCards, setExpandedVehicleCards] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  const [vehicleInfoCache,  setVehicleInfoCache]  = useState({});
  const [animatedPositions, setAnimatedPositions] = useState({});
  const [mediaPreview, setMediaPreview] = useState(null); // { url, mediaType, name } for the click-to-preview popup

  const refreshIntervalRef  = useRef(null);
  const mapContainerRef     = useRef(null);
  const liveMapRef          = useRef(null);
  const geocoderRef         = useRef(null);
  const autocompleteRef     = useRef(null);
  const searchTimerRef      = useRef(null);
  const animFrameRef        = useRef(null);
  const prevPositionsRef    = useRef({});

  // ── Shared loader — same id + libraries as TripAction ─────────────────────
  const { isLoaded, loadError } = useJsApiLoader({
    id: GOOGLE_MAPS_LOADER_ID,
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  const getAddressFromCoordinates = async (lat, lng) => {
    try {
      const res = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${GOOGLE_MAPS_API_KEY}`,
        { signal: AbortSignal.timeout(5000) }
      );
      if (res.ok) {
        const data = await res.json();
        if (data.status === "OK" && data.results?.length > 0) {
          return data.results[0].formatted_address;
        }
      }
    } catch { /* fall through */ }
    return null;
  };

  const fetchAddressesForVehicles = async (vehicles) => {
    setIsLoadingAddresses(true);
    for (let i = 0; i < vehicles.length; i++) {
      const vehicle = vehicles[i];
      const lat = parseFloat(vehicle.latitude);
      const lng = parseFloat(vehicle.longitude);
      const address = !isNaN(lat) && !isNaN(lng)
        ? await getAddressFromCoordinates(lat, lng)
        : "Invalid coordinates";
      setVehicleAddresses((prev) => ({ ...prev, [vehicle.vehicleNumber]: address }));
      if (i < vehicles.length - 1) await new Promise((r) => setTimeout(r, 300));
    }
    setIsLoadingAddresses(false);
  };

  const formatDateDisplay = (dateTimeString) => {
    if (!dateTimeString) return "-";
    try {
      const date = new Date(dateTimeString);
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      let hours = date.getHours();
      const minutes = String(date.getMinutes()).padStart(2, "0");
      const ampm = hours >= 12 ? "PM" : "AM";
      hours = hours % 12 || 12;
      return `${day}-${month}-${year} ${String(hours).padStart(2, "0")}:${minutes} ${ampm}`;
    } catch {
      return dateTimeString;
    }
  };

  const getTimeAgo = (dateTimeString) => {
    if (!dateTimeString) return "-";
    try {
      const diffMs = new Date() - new Date(dateTimeString);
      const diffMins = Math.floor(diffMs / 60000);
      if (diffMins < 1) return "Just now";
      if (diffMins < 60) return `${diffMins} min ago`;
      const diffHours = Math.floor(diffMins / 60);
      if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
      const diffDays = Math.floor(diffHours / 24);
      return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
    } catch {
      return "-";
    }
  };

  useEffect(() => {
    if (isLoaded && window.google) {
      geocoderRef.current = new window.google.maps.Geocoder();
      autocompleteRef.current = new window.google.maps.places.AutocompleteService();
    }
  }, [isLoaded]);

  const searchLocation = useCallback((query) => {
    if (!query.trim()) { setSearchResults([]); return; }
    if (!autocompleteRef.current) return;
    setIsSearching(true);
    autocompleteRef.current.getPlacePredictions(
      { input: query, componentRestrictions: { country: "in" }, types: ["geocode", "establishment"] },
      (predictions, status) => {
        setIsSearching(false);
        const OK = window.google.maps.places.PlacesServiceStatus.OK;
        if (status === OK && predictions?.length) {
          setSearchResults(predictions.map((p) => ({ id: p.place_id, name: p.description, placeId: p.place_id })));
        } else { setSearchResults([]); }
      }
    );
  }, []);

  const selectSearchResult = useCallback((result) => {
    setSearchQuery(result.name);
    setSearchResults([]);
    if (!geocoderRef.current) return;
    geocoderRef.current.geocode({ placeId: result.placeId }, (results, status) => {
      if (status === "OK" && results[0]) {
        const loc = results[0].geometry.location;
        if (liveMapRef.current) {
          if (results[0].geometry.viewport) liveMapRef.current.fitBounds(results[0].geometry.viewport);
          else { liveMapRef.current.panTo({ lat: loc.lat(), lng: loc.lng() }); liveMapRef.current.setZoom(15); }
        }
        toast.success(`Map centred on: ${result.name.split(",")[0]}`);
      }
    });
  }, []);

  const handleSearchInputChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => searchLocation(query), 500);
  };

  const clearSearch = () => {
    setSearchQuery("");
    setSearchResults([]);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
  };

  const fetchLiveVehicles = async (showLoading = true) => {
    if (showLoading) setIsLoadingLive(true);
    try {
      const response = await getData(getlivestatus);
      if (response && Array.isArray(response)) {
        setLiveVehicles(response);
        setLastUpdated(new Date());
        if (Object.keys(vehicleAddresses).length === 0 || showLoading) {
          fetchAddressesForVehicles(response);
        }
      } else {
        toast.error("Failed to fetch live vehicle data");
      }
    } catch {
      toast.error("Failed to fetch live vehicle data");
    } finally {
      if (showLoading) setIsLoadingLive(false);
    }
  };

  // ── Smooth position animation between 5-second live refreshes ────────────────
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (liveVehicles.length === 0) return;
    const ANIM_MS = 3000;
    const newTargets = {};
    liveVehicles.forEach(v => {
      const lat = parseFloat(v.latitude);
      const lng = parseFloat(v.longitude);
      if (!isNaN(lat) && !isNaN(lng)) newTargets[v.vehicleNumber] = { lat, lng };
    });
    const startPositions = { ...prevPositionsRef.current };
    const startTime = performance.now();
    cancelAnimationFrame(animFrameRef.current);
    const tick = (now) => {
      const rawT = (now - startTime) / ANIM_MS;
      const t = Math.min(rawT, 1);
      const eased = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
      const current = {};
      Object.keys(newTargets).forEach(vNum => {
        const start = startPositions[vNum] || newTargets[vNum];
        const end = newTargets[vNum];
        current[vNum] = {
          lat: start.lat + (end.lat - start.lat) * eased,
          lng: start.lng + (end.lng - start.lng) * eased,
        };
      });
      setAnimatedPositions(current);
      // Record the on-screen position every frame so an interrupted animation
      // (new poll, hidden tab) resumes from here instead of snapping back.
      prevPositionsRef.current = { ...prevPositionsRef.current, ...current };
      if (t < 1) animFrameRef.current = requestAnimationFrame(tick);
    };
    animFrameRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(animFrameRef.current);
  }, [liveVehicles]);

  const getVehicleStats = () => ({
    total: liveVehicles.length,
    moving: liveVehicles.filter((v) => v.status === "MOVING").length,
    idle: liveVehicles.filter((v) => v.status === "IDLE").length,
    offline: liveVehicles.filter((v) => v.status === "OFFLINE").length,
    maintenance: liveVehicles.filter((v) => v.status === "MAINTENANCE").length,
  });

  const getFilteredVehicles = () =>
    liveVehicles.filter((vehicle) => {
      const addr = vehicleAddresses[vehicle.vehicleNumber] || "";
      const searchMatch =
        !liveSearchText ||
        vehicle.vehicleNumber.toLowerCase().includes(liveSearchText.toLowerCase()) ||
        addr.toLowerCase().includes(liveSearchText.toLowerCase());
      const statusMatch = selectedStatus === "all" || vehicle.status === selectedStatus;
      const vehicleNumberMatch =
        selectedVehicleNumber === "all" || vehicle.vehicleNumber === selectedVehicleNumber;
      return searchMatch && statusMatch && vehicleNumberMatch;
    });

  const getUniqueVehicleNumbers = () =>
    [...new Set(liveVehicles.map((v) => v.vehicleNumber))].sort();

  const handleVehicleNumberFilterChange = (vehicleNumber) => {
    setSelectedVehicleNumber(vehicleNumber);
    if (vehicleNumber !== "all") {
      const vehicle = liveVehicles.find((v) => v.vehicleNumber === vehicleNumber);
      if (vehicle) handleVehicleCardClick(vehicle);
    } else {
      setSelectedVehicle(null);
      setMapCenter(null);
    }
  };

  const handleStatusCardClick = (status) => {
    setSelectedStatus(selectedStatus === status ? "all" : status);
    setSelectedVehicleNumber("all");
    setSelectedVehicle(null);
    setMapCenter(null);
  };

  const fetchVehicleInfo = async (vehicleNumber) => {
    if (!vehicleNumber || vehicleInfoCache[vehicleNumber]) return;
    try {
      // POST /vehicleinfo/getByVehicleNumber?vehicleNumber=<vehicleNumber>
      const res = await postDataWithBody(`${getVehicleByNumber}?vehicleNumber=${encodeURIComponent(vehicleNumber)}`, {}, { "Content-Type": "application/json" });
      const info = res?.data ?? (Array.isArray(res) ? res[0] : res);
      if (info && typeof info === 'object') {
        setVehicleInfoCache(prev => ({ ...prev, [vehicleNumber]: info }));
      }
    } catch { /* non-critical */ }
  };

  const handleVehicleCardClick = (vehicle) => {
    setSelectedVehicle(vehicle);
    const lat = parseFloat(vehicle.latitude);
    const lng = parseFloat(vehicle.longitude);
    if (!isNaN(lat) && !isNaN(lng)) {
      setMapCenter([lat, lng]);
      mapContainerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    fetchVehicleInfo(vehicle.vehicleNumber);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === "map") {
      setLiveSearchText("");
      setSelectedVehicleNumber("all");
      setSelectedStatus("all");
      setSelectedDistrict("all");
      setSelectedVehicle(null);
      setMapCenter(null);
    }
  };

  useEffect(() => {
    vehicleData([], today, today);
    fetchVehicles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeTab === "map") {
      fetchLiveVehicles(true);
      refreshIntervalRef.current = setInterval(() => fetchLiveVehicles(false), 5000);
    }
    return () => { if (refreshIntervalRef.current) clearInterval(refreshIntervalRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const fetchVehicles = async () => {
    try {
      const response = await getData(getAllVehicles);
      if (Array.isArray(response)) {
        setvehicleTypeList(
          response.map((v) => ({
            id: v.id, value: v.vehicle_number, vehicleName: v.vehicle_number,
          }))
        );
        // Build a lookup map for full vehicle details (used when navigating to track view)
        const map = {};
        response.forEach((v) => {
          map[v.vehicle_number] = {
            vehicleNumber:      v.vehicle_number,
            vehicleType:        v.vehicle_type        || '',
            brand:              v.brand               || '',
            model:              v.model               || '',
            fuelType:           v.fuel_type           || '',
            registrationNumber: v.registration_number || '',
            driverName:         v.driver_name         || '',
            driverPhone:        v.driver_phone        || '',
            driverLicense:      v.driver_license      || '',
            licenseExpiry:      v.license_expiry      || '',
          };
        });
        setAllVehiclesMap(map);
      }
    } catch (error) {
      console.error("Error fetching vehicles:", error);
    }
  };

  const filteredTableData = vehicleTrackingData.filter((item) =>
    Object.values(item).some(
      (value) => value && value.toString().toLowerCase().includes(searchText.toLowerCase())
    )
  );

  useEffect(() => { setRowCount(filteredTableData.length); }, [filteredTableData]);

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    if (value > today) { toast.error("Future dates are not allowed"); return; }
    setFormData((prev) => ({ ...prev, [id]: value }));
    if (validationErrors[id]) setValidationErrors((prev) => ({ ...prev, [id]: "" }));
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.startDate) errors.startDate = "Start date is required";
    if (formData.startDate && formData.endDate && formData.startDate > formData.endDate)
      errors.endDate = "End date must be after or equal to start date";
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const formatDateTimePayload = (dateTime, isEndDate = false) => {
    const date = new Date(dateTime);
    const pad = (n) => String(n).padStart(2, "0");
    if (isEndDate) date.setHours(23, 59, 59, 999);
    else date.setHours(0, 0, 0, 0);
    const ms = String(date.getMilliseconds()).padStart(3, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}.${ms}`;
  };

  const vehicleData = async (vehicleNumbers, startDate, endDate) => {
    const finalEndDate = endDate?.trim() || startDate;
    try {
      const payload = {
        vehicleNumber: vehicleNumbers,
        startTime: formatDateTimePayload(startDate, false),
        endTime: formatDateTimePayload(finalEndDate, true),
      };
      const response = await postDataWithBody(getVehicleDataForTracking, payload, {});
      setVehicleTrackingData(response);
      setRowCount(response.length);
      setSearchText("");
    } catch (err) {
      console.error("Failed to execute query:", err);
      toast.error("Failed to execute query");
      setVehicleTrackingData([]);
      setRowCount(0);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    try {
      await vehicleData(formData.vehicleType, formData.startDate, formData.endDate);
    } catch {
      alert("Error submitting form. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setFormData({ startDate: "", endDate: "", vehicleType: [] });
    setValidationErrors({});
    setVehicleTrackingData([]);
    setRowCount(0);
  };

  const columns = React.useMemo(
    () => [
      {
        Header: t("S.No."),
        accessor: (row, index) => index + 1,
        disableSortBy: true,
        Cell: ({ row }) => <span>{row.index + 1}</span>,
      },
      { Header: t("Vehicle Number"), accessor: "vehicleNumber", disableSortBy: true },
      {
        Header: t("Start Date & Time"),
        accessor: "startTime",
        disableSortBy: true,
        Cell: ({ value }) => formatDateDisplay(value),
      },
      {
        Header: t("End Date & Time"),
        accessor: "endTime",
        disableSortBy: true,
        Cell: ({ value }) => formatDateDisplay(value),
      },
      {
        Header: t("Track View"),
        disableSortBy: true,
        Cell: ({ row }) => (
          <div className="usertable__actionsicon">
            <span onClick={() => {
              const vNum = (row.original.vehicleNumber || '').trim();
              const vData = allVehiclesMap[vNum] || null;
              navigate("/my-gps-tracking/gps-track-view", {
                state: {
                  data: row.original,
                  edit: true,
                  vehicleType: vData?.vehicleType || '',
                  vehicleData: vData,
                },
              });
            }}>
              <MapPin className="cursor-pointer me-2" size={16} color="blue" />
            </span>
          </div>
        ),
      },
    ],
    [navigate, allVehiclesMap]
  );

  const vehicleStats = getVehicleStats();
  const filteredVehicles = getFilteredVehicles();

  return (
    <>
      <div className="branch-main-container gps-tracking-container" style={{padding:"20px 0 20px 0"}}>
        {/* Tab Navigation */}
        <div style={{
          padding: '12px 20px 10px',
          marginLeft: '20px', marginRight: '20px',
          background: '#edf2f9',
          borderRadius: '14px 14px 0 0',
          display: 'flex', alignItems: 'center', gap: 8,
          borderBottom: '1px solid #d5e4f0',
        }}>
          <div style={{
            display: 'inline-flex', background: 'rgba(20,92,87,0.12)',
            borderRadius: 11, padding: 4, gap: 2,
          }}>
            {[
              { id: "map", label: "Active Panel", icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21"/><line x1="9" y1="3" x2="9" y2="18"/><line x1="15" y1="6" x2="15" y2="21"/></svg> },
              { id: "form", label: "GPS Tracking", icon: <Navigation size={13} /> },
            ].map((tab) => {
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  style={{
                    padding: '7px 20px', border: 'none', cursor: 'pointer',
                    background: isActive
                      ? 'linear-gradient(135deg, #145c57 0%, #1a7a73 100%)'
                      : 'transparent',
                    color: isActive ? '#fff' : '#4e6a82',
                    fontWeight: isActive ? 700 : 500, fontSize: 13,
                    display: 'flex', alignItems: 'center', gap: 7,
                    borderRadius: 8,
                    boxShadow: isActive ? '0 2px 10px rgba(20,92,87,0.35)' : 'none',
                    transition: 'all 0.22s', letterSpacing: '0.15px', outline: 'none',
                  }}
                >
                  {tab.icon}{tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {activeTab === "form" ? (
          <>
            {/* GPS Tracking Tab — filter bar */}
            <div style={{
              background: '#ffffff', border: '1px solid #d8e8f3',
              borderRadius: '13px', padding: 0,
              marginBottom: '14px',
              marginLeft: '20px', marginRight: '20px', marginTop: '16px',
              boxShadow: '0 4px 18px rgba(20,92,87,0.09)',
            }}>
              {/* Filter bar header */}
              <div style={{
                // Dot pattern layered over the gradient (a separate backgroundImage would wipe the gradient out)
                backgroundColor: '#0f4a45',
                backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.06) 1px, transparent 1px), linear-gradient(135deg, #0a2e2c 0%, #0f4a45 45%, #145c57 100%)',
                backgroundSize: '18px 18px, 100% 100%',
                borderRadius: '13px 13px 0 0',
                padding: '11px 20px',
                display: 'flex', alignItems: 'center', gap: 10,
              }}>
                <div style={{ width: 30, height: 30, borderRadius: 8, background: 'rgba(255,255,255,0.16)', border: '1px solid rgba(255,255,255,0.26)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Navigation size={14} color="white" />
                </div>
                <span style={{ fontWeight: 700, fontSize: 13, color: '#fff', letterSpacing: '0.2px' }}>GPS Track History Search</span>
              </div>
              <div style={{ padding: '14px 18px', display: 'flex', alignItems: 'flex-start', gap: '12px', flexWrap: 'nowrap' }}>
                {/* Vehicle Number */}
                <div style={{ flex: '0 0 320px' }}>
                  <label style={{ fontSize: 11, fontWeight: 700, color: '#1a7a73', marginBottom: 5, display: 'block', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                    Vehicle Number
                  </label>
                  <MultiSelectDropdown
                    options={vehicleTypeList}
                    selectedValues={formData.vehicleType}
                    onChange={(sel) => {
                      setFormData((prev) => ({ ...prev, vehicleType: sel }));
                      if (validationErrors.vehicleType)
                        setValidationErrors((prev) => ({ ...prev, vehicleType: "" }));
                    }}
                    placeholder="Select Vehicle Numbers"
                    error={validationErrors.vehicleType}
                  />
                  {validationErrors.vehicleType && (
                    <div className="error-message">{validationErrors.vehicleType}</div>
                  )}
                </div>

                {/* Dates + Buttons — always inline */}
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', flexWrap: 'nowrap', flexShrink: 0 }}>
                  {["startDate", "endDate"].map((field) => (
                    <div key={field} style={{ flex: '0 0 175px' }}>
                      <label htmlFor={field} style={{ fontSize: 11, fontWeight: 700, color: '#1a7a73', marginBottom: 5, display: 'block', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                        {field === "startDate" ? "Start Date" : "End Date"}
                        {field === "startDate" && <span style={{ color: '#dc3545' }}> *</span>}
                      </label>
                      <div className="date-input-wrapper">
                        <input
                          type="date"
                          className={`form-control date-input-custom padd-value ${validationErrors[field] ? "is-invalid" : ""}`}
                          id={field}
                          value={formData[field]}
                          onChange={handleInputChange}
                          max={today}
                          style={{ fontSize: 13, height: 38 }}
                        />
                        <Calendar className="date-icon" size={14} style={{ marginRight: validationErrors[field] ? "13px" : "0px" }} />
                      </div>
                      {validationErrors[field] && (
                        <div className="error-message">{validationErrors[field]}</div>
                      )}
                    </div>
                  ))}

                  <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, paddingTop: 22 }}>
                    <button
                      onClick={handleSubmit}
                      disabled={isSubmitting}
                      style={{
                        height: 38, padding: '0 20px', border: 'none', borderRadius: 7,
                        background: isSubmitting ? '#94a3b8' : 'linear-gradient(135deg, #28a745 0%, #1e7e34 100%)',
                        color: 'white', fontWeight: 600, fontSize: 13,
                        cursor: isSubmitting ? 'not-allowed' : 'pointer',
                        display: 'flex', alignItems: 'center', gap: 6,
                        boxShadow: '0 2px 6px rgba(40,167,69,0.3)',
                      }}
                    >
                      <Search size={14} />
                      {isSubmitting ? "Searching..." : "Search"}
                    </button>
                    <button
                      type="button"
                      onClick={handleCancel}
                      style={{
                        height: 38, padding: '0 18px', border: '1.5px solid #dee2e6', borderRadius: 7,
                        background: '#f8f9fa', color: '#495057', fontWeight: 600, fontSize: 13,
                        cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
                      }}
                    >
                      <RefreshCw size={13} />
                      Reset
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="row" style={{ marginLeft: '20px', marginRight: '20px' }}>
              <div className="col-md-12" style={{ padding: 0 }}>
                <div className="gps-tracking-table">
                  <Table
                    data={vehicleTrackingData}
                    title=""
                    showFilter={false}
                    columns={columns}
                    numberOfRows={itemsPerPage}
                    showExport={false}
                    showReset={false}
                    onExpand={false}
                    showSearch={true}
                    isHeaderFixed={true}
                    rowCount={rowCount}
                    headerTableHeadStyle={{ background: 'linear-gradient(135deg, #145c57 0%, #1a7a73 100%)', color: "#FFFFFF" }}
                    tableStyle={{ border: "1px solid #e2e8f0", borderRadius: "8px", marginTop: "4px", padding: "0px", overflow: "hidden" }}
                  />
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="live-panel-container" style={{ padding: 0, background: '#edf2f9', borderRadius: '0 0 16px 16px', marginLeft: '20px', marginRight: '20px', overflow: 'hidden', border: '1px solid #dde4ef' }}>

            {/* ── TOP STATS STRIP ─────────────────────────────────────────────── */}
            <div style={{ background: '#fff', borderBottom: '1px solid #e2e8f0', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              {/* Fleet header */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginRight: 4 }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: 'linear-gradient(135deg,#145c57,#1a7a73)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 4px 10px rgba(20,92,87,0.25)' }}>
                  <Car size={16} color="white" />
                </div>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontWeight: 800, fontSize: 13, color: '#1e293b', letterSpacing: '-0.2px' }}>Fleet Overview</span>
                    <span className="live-pulse-dot" style={{ width: 6, height: 6, borderRadius: '50%', background: '#22c55e', display: 'inline-block' }} />
                    <span style={{ fontSize: 10, color: '#64748b', fontWeight: 500 }}>Live</span>
                  </div>
                  {lastUpdated && (
                    <span style={{ fontSize: 10, color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 3, marginTop: 1 }}>
                      <Clock size={9} color="#94a3b8" />
                      {lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  )}
                </div>
                <button onClick={() => fetchLiveVehicles(true)} disabled={isLoadingLive} style={{ marginLeft: 4, padding: '5px 12px', border: '1.5px solid #d0dbe8', borderRadius: 8, background: '#f8fafc', color: '#475569', fontSize: 10, fontWeight: 600, cursor: isLoadingLive ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <RefreshCw size={10} className={isLoadingLive ? "spinning" : ""} /> Refresh
                </button>
              </div>

              {/* Divider */}
              <div style={{ width: 1, height: 36, background: '#e2e8f0', flexShrink: 0 }} />

              {/* KPI chips */}
              {[
                { key: "all", label: "Total Vehicles", count: vehicleStats.total, color: "#1a7a73", icon: <Car size={14} color="#1a7a73" /> },
                { key: "MOVING", label: "Moving", count: vehicleStats.moving, color: "#1a7a73", icon: <Play size={14} color="#1a7a73" /> },
                { key: "IDLE", label: "Idle", count: vehicleStats.idle, color: "#ea580c", icon: <Pause size={14} color="#ea580c" /> },
                { key: "OFFLINE", label: "Offline", count: vehicleStats.offline, color: "#64748b", icon: <Power size={14} color="#64748b" /> },
                ...(vehicleStats.maintenance > 0 ? [{ key: "MAINTENANCE", label: "Maint.", count: vehicleStats.maintenance, color: "#0891b2", icon: <AlertCircle size={14} color="#0891b2" /> }] : []),
              ].map((card) => {
                const isActive = selectedStatus === card.key;
                return (
                  <div
                    key={card.key}
                    className={`stat-card-clickable ${isActive ? "active" : ""}`}
                    onClick={() => handleStatusCardClick(card.key)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '10px 14px', borderRadius: 10, cursor: 'pointer',
                      background: '#fff',
                      border: `1.5px solid ${isActive ? card.color : 'transparent'}`,
                      boxShadow: isActive ? `0 4px 14px rgba(0,0,0,0.18)` : '0 2px 8px rgba(0,0,0,0.12)',
                      transition: 'all 0.22s', flex: 1, position: 'relative', overflow: 'hidden',
                    }}
                  >
                    {/* colored top accent bar */}
                    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: isActive ? card.color : `${card.color}55`, borderRadius: '10px 10px 0 0' }} />
                    <div style={{ width: 32, height: 32, borderRadius: '50%', background: `${card.color}14`, display: 'flex', alignItems: 'center', justifyContent: 'center', border: `1.5px solid ${card.color}28`, flexShrink: 0 }}>
                      {card.icon}
                    </div>
                    <div>
                      <div style={{ fontSize: 22, fontWeight: 800, color: card.color, lineHeight: 1, letterSpacing: '-0.5px' }}>{card.count}</div>
                      <div style={{ fontSize: 9, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.6px', marginTop: 2 }}>{card.label}</div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* ── MAIN BODY: Sidebar + Map ─────────────────────────────────────── */}
            <div className="row" style={{ margin: 0, alignItems: 'stretch' }}>

              {/* LEFT SIDEBAR */}
              <div className="col-lg-3" style={{ padding: 0, display: 'flex', flexDirection: 'column', borderRight: '1px solid #e2e8f0', background: '#f8fafc' }}>

                {/* Filters & Search */}
                <div style={{ borderBottom: '1px solid #e2e8f0' }}>
                  <div style={{ background: 'linear-gradient(135deg, #0a2e2c 0%, #0f4a45 45%, #145c57 100%)', padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 26, height: 26, borderRadius: 7, background: 'rgba(255,255,255,0.16)', border: '1px solid rgba(255,255,255,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>
                    </div>
                    <span style={{ fontWeight: 700, fontSize: 12, color: '#fff', letterSpacing: '0.2px' }}>Filters &amp; Search</span>
                  </div>
                  <div style={{ padding: '10px 14px 12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', border: '1.5px solid #e2e8f0', borderRadius: 8, overflow: 'hidden', background: '#fff', marginBottom: 10 }}>
                    <span style={{ padding: '0 9px', color: '#94a3b8' }}><Search size={12} /></span>
                    <input type="text" placeholder="Search vehicle, address..." value={liveSearchText} onChange={(e) => setLiveSearchText(e.target.value)}
                      style={{ flex: 1, border: 'none', outline: 'none', background: 'transparent', fontSize: 12, padding: '8px 4px 8px 0', color: '#334155' }} />
                  </div>
                  <label style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: 4, marginBottom: 5 }}>
                    <Car size={10} color="#1a7a73" /> Vehicle Number
                  </label>
                  <select className="form-select form-select-sm" value={selectedVehicleNumber} onChange={(e) => handleVehicleNumberFilterChange(e.target.value)}
                    style={{ fontSize: 12, border: '1.5px solid #d0dbe8', borderRadius: 7, padding: '7px 8px', background: '#f8faff', color: '#334155', cursor: 'pointer', outline: 'none', marginBottom: 10 }}>
                    <option value="all">All Vehicles</option>
                    {getUniqueVehicleNumbers().map((vn, i) => (
                      <option key={i} value={vn}>{vn}</option>
                    ))}
                  </select>
                  {(() => {
                    // Only clickable once a filter or vehicle is actually applied
                    const hasActiveFilter = !!liveSearchText || selectedVehicleNumber !== "all" || selectedStatus !== "all" || selectedDistrict !== "all" || !!selectedVehicle;
                    return (
                  <button disabled={!hasActiveFilter} onClick={() => { setLiveSearchText(""); setSelectedVehicleNumber("all"); setSelectedDistrict("all"); setSelectedStatus("all"); setSelectedVehicle(null); setMapCenter(null); toast.info("Filters cleared"); }}
                    style={{ width: '100%', padding: '7px 0', border: `1.5px solid ${hasActiveFilter ? '#1a7a73' : '#cbd5e1'}`, borderRadius: 7, background: 'transparent', color: hasActiveFilter ? '#1a7a73' : '#94a3b8', fontSize: 11, fontWeight: 700, cursor: hasActiveFilter ? 'pointer' : 'not-allowed', opacity: hasActiveFilter ? 1 : 0.7, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}>
                    <X size={11} /> Clear Filters
                  </button>
                    );
                  })()}
                  {(selectedVehicleNumber !== "all" || selectedStatus !== "all") && (
                    <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {selectedVehicleNumber !== "all" && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#ccfbf1', color: '#1a7a73', border: '1px solid rgba(20,92,87,0.3)', borderRadius: 12, padding: '2px 7px', fontSize: 10, fontWeight: 600 }}>
                          {selectedVehicleNumber}<X size={10} style={{ cursor: "pointer" }} onClick={() => handleVehicleNumberFilterChange("all")} />
                        </span>
                      )}
                      {selectedStatus !== "all" && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#f1f5f9', color: '#475569', border: '1px solid #dee2e6', borderRadius: 12, padding: '2px 7px', fontSize: 10, fontWeight: 600 }}>
                          {selectedStatus}<X size={10} style={{ cursor: "pointer" }} onClick={() => setSelectedStatus("all")} />
                        </span>
                      )}
                    </div>
                  )}
                  </div>{/* end padding wrapper */}
                </div>{/* end filters section */}

                {/* Vehicle List */}
                <div style={{ flex: vehicleDetailsOpen ? '1 1 auto' : '0 0 auto', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                  <div onClick={() => setVehicleDetailsOpen(v => !v)} style={{ padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none', background: vehicleDetailsOpen ? 'linear-gradient(135deg, #0a2e2c 0%, #145c57 100%)' : 'linear-gradient(135deg, #0f4a45 0%, #1a7a73 100%)', borderBottom: '1px solid rgba(255,255,255,0.1)', transition: 'background 0.3s', boxShadow: vehicleDetailsOpen ? '0 3px 10px rgba(0,0,0,0.15)' : 'none' }}>
                    <div style={{ width: 28, height: 28, borderRadius: 7, background: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.28)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Car size={13} color="white" /></div>
                    <span style={{ fontWeight: 700, fontSize: 12, color: 'white', flex: 1 }}>Vehicles</span>
                    <span style={{ background: 'rgba(255,255,255,0.22)', color: 'white', border: '1px solid rgba(255,255,255,0.32)', borderRadius: 20, padding: '2px 9px', fontSize: 10, fontWeight: 700 }}>{filteredVehicles.length}</span>
                    <span style={{ color: 'rgba(255,255,255,0.85)', display: 'flex', transform: vehicleDetailsOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.3s', background: 'rgba(255,255,255,0.14)', borderRadius: '50%', padding: 4 }}><ChevronDown size={13} /></span>
                  </div>
                  {vehicleDetailsOpen && (
                    <div className="vehicle-list-container" style={{ flex: 1, overflowY: 'auto', maxHeight: 340 }}>
                      {isLoadingLive ? (
                        <div style={{ textAlign: 'center', padding: '20px 0' }}><div className="spinner-border spinner-border-sm" role="status" style={{ color: '#1a7a73' }} /></div>
                      ) : filteredVehicles.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '24px 0', color: '#94a3b8' }}><Car size={32} style={{ opacity: 0.3, marginBottom: 8 }} /><p style={{ margin: 0, fontSize: 12 }}>No vehicles found</p></div>
                      ) : filteredVehicles.map((vehicle, index) => {
                        const isSelected = selectedVehicle?.vehicleNumber === vehicle.vehicleNumber;
                        const statusStyle = getStatusBadgeStyle(vehicle.status);
                        const statusColor = statusStyle.background;
                        const address = vehicleAddresses[vehicle.vehicleNumber];
                        const eventType = vehicle.eventType || vehicle.alertType || "STABLE";
                        const hasEvent = eventType && !["STABLE", "NORMAL", "HALT"].includes(eventType.toUpperCase());
                        return (
                          <div key={index} onClick={() => handleVehicleCardClick(vehicle)}
                            style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '9px 12px', background: isSelected ? '#f0fdfb' : '#fff', borderLeft: `3px solid ${isSelected ? '#1a7a73' : statusColor}`, borderBottom: '1px solid #f1f5f9', cursor: 'pointer', transition: 'background 0.15s' }}>
                            <div style={{ width: 7, height: 7, borderRadius: '50%', background: statusColor, flexShrink: 0, animation: vehicle.status === "MOVING" ? "pulse 2s infinite" : "none" }} />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                <span style={{ fontWeight: 700, fontSize: 11, color: isSelected ? '#1a7a73' : '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{vehicle.vehicleNumber}</span>
                                {hasEvent && <span style={{ fontSize: 8, padding: '1px 4px', borderRadius: 6, background: '#fef2f2', color: '#dc2626', border: '1px solid #fca5a5', fontWeight: 700, flexShrink: 0 }}>ALERT</span>}
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                                <span style={{ fontSize: 10, color: '#64748b', display: 'flex', alignItems: 'center', gap: 2 }}><Navigation size={8} color="#1a7a73" />{vehicle.speed ?? 0} km/h</span>
                                <span style={{ fontSize: 10, color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 2 }}><Clock size={8} color="#94a3b8" />{getTimeAgo(vehicle.vehicleTrackingTime)}</span>
                              </div>
                              {address && <div style={{ fontSize: 10, color: '#b0b8c8', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{address}</div>}
                            </div>
                            <span style={{ fontSize: 9, padding: '2px 5px', borderRadius: 7, background: `${statusColor}18`, color: statusColor, border: `1px solid ${statusColor}40`, fontWeight: 700, flexShrink: 0 }}>{vehicle.status}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Selected Vehicle Info */}
                {selectedVehicle && (() => {
                  const vInfo = vehicleInfoCache[selectedVehicle.vehicleNumber];
                  const v = vInfo || {};
                  const SRow = ({ label, value }) => value ? (
                    <div style={{ display: 'grid', gridTemplateColumns: '85px 1fr', gap: 4, padding: '4px 0', borderBottom: '1px solid #f1f5f9' }}>
                      <span style={{ color: '#94a3b8', fontSize: 10, fontWeight: 600 }}>{label}</span>
                      <span style={{ color: '#1e293b', fontSize: 10, textAlign: 'right', wordBreak: 'break-word' }}>{value}</span>
                    </div>
                  ) : null;
                  const SHead = ({ children }) => (
                    <div style={{ fontSize: 9, fontWeight: 700, color: '#1a7a73', background: '#f0fdfb', padding: '3px 12px', margin: '6px -12px 4px', textTransform: 'uppercase', letterSpacing: '0.5px', borderLeft: '2px solid #1a7a73' }}>{children}</div>
                  );
                  return (
                    <div style={{ borderTop: '1px solid #e2e8f0' }}>
                      <div onClick={() => setVehicleInfoPanelOpen(o => !o)} style={{ background: vehicleInfoPanelOpen ? 'linear-gradient(135deg, #0a2e2c 0%, #145c57 100%)' : 'linear-gradient(135deg, #0f4a45 0%, #1a7a73 100%)', padding: '9px 12px', display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none', boxShadow: vehicleInfoPanelOpen ? '0 3px 10px rgba(0,0,0,0.15)' : 'none' }}>
                        <span style={{ fontSize: 16 }}>{getVehicleEmoji(v.vehicle_type)}</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 700, fontSize: 11, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selectedVehicle.vehicleNumber}</div>
                          {v.vehicle_type && <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.7)', marginTop: 1 }}>{v.vehicle_type}</div>}
                        </div>
                        <span style={{ color: 'rgba(255,255,255,0.85)', display: 'flex', transform: vehicleInfoPanelOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.3s', background: 'rgba(255,255,255,0.14)', borderRadius: '50%', padding: 4 }}><ChevronDown size={13} /></span>
                      </div>
                      {vehicleInfoPanelOpen && (!vInfo ? (
                        <div style={{ padding: '12px', fontSize: 11, color: '#94a3b8', fontStyle: 'italic', textAlign: 'center' }}>Loading vehicle details…</div>
                      ) : (
                        <div style={{ padding: '6px 12px 12px' }}>
                          <SHead>Vehicle Details</SHead>
                          <SRow label="Brand" value={v.brand} />
                          <SRow label="Model" value={v.model} />
                          <SRow label="Fuel Type" value={v.fuel_type} />
                          <SRow label="RC No." value={v.rc_number} />
                          <SHead>Compliance</SHead>
                          <SRow label="Ins. Expiry" value={v.insurance_expiry ? v.insurance_expiry.split('-').reverse().join('-') : null} />
                          <SRow label="PUC Expiry" value={v.puc_expiry ? v.puc_expiry.split('-').reverse().join('-') : null} />
                          <SHead>Driver Details</SHead>
                          <SRow label="Name" value={v.driver_name} />
                          <SRow label="Phone" value={v.driver_phone} />
                          <SRow label="License No." value={v.license_number} />
                          {Array.isArray(v.image_paths) && v.image_paths.length > 0 && (
                            <>
                              <SHead>Photos / Videos</SHead>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, paddingTop: 4 }}>
                                {v.image_paths.map((url, i) => {
                                  const mediaType = guessMediaTypeFromUrl(url);
                                  return (
                                    <div
                                      key={`${url}-${i}`}
                                      onClick={() => setMediaPreview({ url, mediaType, name: decodeURIComponent(url.split('/').pop() || '') })}
                                      style={{ position: 'relative', width: 52, height: 52, borderRadius: 6, overflow: 'hidden', border: '1px solid #e2e8f0', cursor: 'pointer', flexShrink: 0, background: '#0f172a' }}
                                    >
                                      {mediaType === 'image' ? (
                                        <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} onError={(e) => { e.target.style.display = 'none'; }} />
                                      ) : (
                                        <video src={url} preload="metadata" muted playsInline style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                                      )}
                                      {mediaType === 'video' && (
                                        <span style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 18, height: 18, borderRadius: '50%', background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                                          <svg width="8" height="8" viewBox="0 0 24 24" fill="white"><polygon points="6 4 20 12 6 20"/></svg>
                                        </span>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>

              {/* RIGHT: MAP */}
              <div className="col-lg-9" style={{ padding: 0, display: 'flex', flexDirection: 'column' }}>
                {/* Map toolbar */}
                <div style={{ padding: '8px 14px', background: 'linear-gradient(to right, #0f3d3a 0%, #145c57 55%, #1a7a73 100%)', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                  <div style={{ width: 26, height: 26, borderRadius: 7, background: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.28)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Car size={13} color="white" />
                  </div>
                  <span style={{ color: '#fff', fontWeight: 700, fontSize: 13, flex: 1, letterSpacing: '-0.1px' }}>GPS Tracking Live Map</span>
                  <span style={{ background: 'rgba(255,255,255,0.18)', color: '#fff', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 20, padding: '2px 10px', fontSize: 10, fontWeight: 600, flexShrink: 0 }}>
                    {(MAP_TYPE_OPTIONS.find(o => o.id === mapType)?.label ?? mapType)} layer
                  </span>
                  {/* Search bar inline in toolbar */}
                  <div style={{ position: 'relative', flex: '0 0 280px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', background: '#fff', border: '1.5px solid rgba(255,255,255,0.6)', borderRadius: 8, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.15)' }}>
                      <div style={{ padding: '0 9px', color: '#94a3b8', display: 'flex', alignItems: 'center' }}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                      </div>
                      <input type="text" placeholder="Search city, area, landmark…" value={searchQuery} onChange={handleSearchInputChange}
                        style={{ flex: 1, border: 'none', outline: 'none', fontSize: 12, padding: '7px 4px', color: '#1e293b', background: 'transparent' }} />
                      {searchQuery && <button type="button" onClick={clearSearch} style={{ padding: '0 9px', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 14, display: 'flex', alignItems: 'center' }}>✕</button>}
                      {isSearching && <div style={{ padding: '0 9px' }}><div className="spinner-border spinner-border-sm text-primary" role="status" style={{ width: 12, height: 12, borderWidth: 2 }} /></div>}
                    </div>
                    {searchResults.length > 0 && (
                      <div style={{ position: 'absolute', left: 0, right: 0, top: '100%', marginTop: 2, zIndex: 1050, background: '#fff', borderRadius: 10, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                        {searchResults.map((result) => (
                          <button key={result.id} type="button" onClick={() => selectSearchResult(result)} className="search-result-row"
                            style={{ width: '100%', padding: '9px 12px', background: 'none', border: 'none', borderBottom: '1px solid #f1f5f9', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'flex-start', gap: 9 }}>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginTop: 2, flexShrink: 0 }}><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/><circle cx="12" cy="9" r="2.5"/></svg>
                            <div>
                              <div style={{ fontSize: 12, fontWeight: 600, color: '#1e293b' }}>{result.name.split(",")[0]}</div>
                              <div style={{ fontSize: 10, color: '#64748b' }}>{result.name.split(",").slice(1, 3).join(",")}</div>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Map itself */}
                <div ref={mapContainerRef} style={{ flex: 1, position: 'relative', minHeight: 520, overflow: 'hidden', isolation: 'isolate' }}>
                  {!isLoaded ? (
                    <div className="d-flex justify-content-center align-items-center h-100">
                      <div className="spinner-border" role="status" style={{ color: '#1a7a73' }}><span className="visually-hidden">Loading...</span></div>
                    </div>
                  ) : loadError ? (
                    <div className="alert alert-danger m-3">Failed to load Google Maps.</div>
                  ) : isLoadingLive ? (
                    <div className="d-flex justify-content-center align-items-center h-100">
                      <div className="spinner-border" role="status" style={{ color: '#1a7a73' }}><span className="visually-hidden">Loading...</span></div>
                    </div>
                  ) : (
                    <LiveGoogleMap
                      vehicles={filteredVehicles}
                      selectedVehicle={selectedVehicle}
                      mapCenter={mapCenter}
                      vehicleAddresses={vehicleAddresses}
                      mapType={mapType}
                      setMapType={setMapType}
                      showTraffic={showTraffic}
                      setShowTraffic={setShowTraffic}
                      formatDateDisplay={formatDateDisplay}
                      getMapRef={(m) => { liveMapRef.current = m; }}
                      animatedPositions={animatedPositions}
                      vehicleInfoCache={vehicleInfoCache}
                      allVehiclesMap={allVehiclesMap}
                      onMarkerClick={(v) => fetchVehicleInfo(v.vehicleNumber)}
                    />
                  )}
                </div>
              </div>

            </div>
          </div>
        )}
      </div>

      {mediaPreview && (
        <div
          onClick={() => setMediaPreview(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.82)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
        >
          <button
            type="button"
            onClick={() => setMediaPreview(null)}
            title="Close"
            style={{ position: 'absolute', top: 20, right: 20, width: 36, height: 36, borderRadius: '50%', border: 'none', background: 'rgba(255,255,255,0.15)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
          {mediaPreview.mediaType === 'image' ? (
            <img
              src={mediaPreview.url} alt={mediaPreview.name}
              onClick={(e) => e.stopPropagation()}
              style={{ maxWidth: '90vw', maxHeight: '85vh', borderRadius: 8, objectFit: 'contain', boxShadow: '0 8px 40px rgba(0,0,0,0.5)' }}
            />
          ) : (
            <video
              src={mediaPreview.url} controls autoPlay
              onClick={(e) => e.stopPropagation()}
              style={{ maxWidth: '90vw', maxHeight: '85vh', borderRadius: 8, boxShadow: '0 8px 40px rgba(0,0,0,0.5)' }}
            />
          )}
        </div>
      )}

      <style jsx>{`
        .gps-tracking-container { padding: 0; }
        .live-pulse-dot { animation: livePulse 2.5s ease-in-out infinite; }
        @keyframes livePulse { 0%, 100% { box-shadow: 0 0 0 2px rgba(34,197,94,0.3); } 50% { box-shadow: 0 0 0 6px rgba(34,197,94,0.08); } }
        .form-field-container { position: relative; min-height: 85px; display: flex; flex-direction: column; }
        .error-message { color: #dc3545; font-size: 11px; margin-top: 3px; white-space: nowrap; }
        .multi-select-container { position: relative; width: 100%; }
        .multi-select-trigger { display: flex; align-items: center; justify-content: space-between; padding: 8px 12px; border: 1.5px solid #e2e8f0; border-radius: 7px; background-color: white; cursor: pointer; min-height: 38px; transition: all 0.2s; }
        .multi-select-trigger:hover { border-color: #1a7a73; }
        .multi-select-trigger.open { border-color: #1a7a73; box-shadow: 0 0 0 3px rgba(20,92,87,0.12); }
        .multi-select-trigger.is-invalid { border-color: #dc3545; }
        .selected-tags-inline { flex: 1; display: flex; flex-wrap: wrap; gap: 4px; }
        .placeholder-text { color: #94a3b8; font-size: 13px; }
        .chevron-icon { transition: transform 0.2s; color: #94a3b8; }
        .chevron-icon.rotated { transform: rotate(180deg); }
        .multi-select-dropdown { position: absolute; top: 100%; left: 0; right: 0; background: white; border: 1.5px solid #e2e8f0; border-radius: 8px; box-shadow: 0 6px 20px rgba(0,0,0,0.12); z-index: 1000; max-height: 300px; overflow: hidden; margin-top: 4px; }
        .search-container { position: relative; padding: 8px; border-bottom: 1px solid #f0f4f8; }
        .search-input { width: 100%; padding: 6px 8px; border: 1.5px solid #e2e8f0; border-radius: 6px; font-size: 12px; outline: none; }
        .search-input:focus { border-color: #1a7a73; }
        .select-option { display: flex; align-items: center; padding: 8px 12px; cursor: pointer; transition: background-color 0.2s; }
        .select-option:hover { background-color: #f0f7ff; }
        .select-option.select-all { font-weight: 600; color: #1a7a73; }
        .checkbox-container { margin-right: 8px; }
        .custom-checkbox { width: 16px; height: 16px; border: 2px solid #cbd5e1; border-radius: 3px; display: flex; align-items: center; justify-content: center; background: white; transition: all 0.2s; }
        .custom-checkbox.checked { background-color: #1a7a73; border-color: #1a7a73; color: white; }
        .option-text { font-size: 13px; color: #495057; }
        .divider { height: 1px; background-color: #f0f4f8; margin: 4px 0; }
        .options-container { max-height: 150px; overflow-y: auto; }
        .no-options { padding: 12px; text-align: center; color: #94a3b8; font-size: 12px; }
        .tag { display: inline-flex; align-items: center; gap: 4px; background-color: #ccfbf1; color: #1a7a73; padding: 2px 8px; border-radius: 12px; font-size: 12px; white-space: nowrap; border: 1px solid #1a7a7333; }
        .tag-remove { cursor: pointer; }
        .date-input-wrapper { position: relative; display: flex; align-items: center; }
        .date-icon { position: absolute; right: 12px; color: #94a3b8; pointer-events: none; z-index: 1; }
        .date-input-custom { appearance: none; -webkit-appearance: none; padding-right: 40px; }
        .date-input-custom::-webkit-calendar-picker-indicator { background: transparent; bottom: 0; color: transparent; cursor: pointer; height: auto; left: 0; position: absolute; right: 0; top: 0; width: auto; z-index: 2; }
        .live-panel-container { animation: fadeIn 0.3s ease-in; }
        .stat-card-clickable { transition: all 0.25s ease; }
        .stat-card-clickable:hover { transform: translateY(-4px); box-shadow: 0 8px 20px rgba(0,0,0,0.12) !important; }
        .stat-card-clickable.active { transform: scale(1); }
        .vehicle-card { transition: all 0.2s; }
        .vehicle-card:hover { transform: translateX(3px); box-shadow: 0 4px 12px rgba(20,92,87,0.12) !important; }
        .vehicle-list-container { scrollbar-width: thin; scrollbar-color: #1a7a73 #f1f5f9; }
        .vehicle-list-container::-webkit-scrollbar { width: 5px; }
        .vehicle-list-container::-webkit-scrollbar-track { background: #f1f5f9; border-radius: 10px; }
        .vehicle-list-container::-webkit-scrollbar-thumb { background: #1a7a73; border-radius: 10px; }
        .vehicle-list-container::-webkit-scrollbar-thumb:hover { background: #155a8a; }
        .gps-tracking-table .iccc__table-header { display: none !important; }
        .gps-tracking-table .iccc--table thead th {
          padding: 13px 16px !important;
          font-size: 11px !important;
          font-weight: 700 !important;
          letter-spacing: 0.7px !important;
          text-transform: uppercase !important;
          border: none !important;
          white-space: nowrap !important;
          border-bottom: 2px solid rgba(255,255,255,0.15) !important;
        }
        .gps-tracking-table .iccc--table tbody tr,
        .gps-tracking-table .iccc--table tbody td {
          border: none !important;
          border-color: transparent !important;
        }
        .gps-tracking-table .table > :not(caption) > * > * {
          border-bottom-width: 0 !important;
        }
        .gps-tracking-table .iccc--table tbody td {
          padding: 10px 16px !important;
          vertical-align: middle !important;
        }
        .gps-tracking-table .iccc--table tbody tr:nth-child(odd) td {
          background-color: #ffffff !important;
        }
        .gps-tracking-table .iccc--table tbody tr:nth-child(even) td {
          background-color: #f4f8fd !important;
        }
        .gps-tracking-table .iccc--table tbody tr:hover td {
          background-color: #e3eef9 !important;
          transition: background-color 0.12s ease;
        }
        .gps-tracking-table .iccc--table tbody tr:hover td:first-child {
          box-shadow: inset 3px 0 0 #1a7a73 !important;
        }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes fadeInPanel { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
        .spinning { animation: spin 1s linear infinite; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .btn-tab:hover { color: #1a7a73 !important; background-color: rgba(20,92,87,0.07) !important; border-radius: 6px 6px 0 0; }
        @media (max-width: 768px) {
          .multi-select-dropdown { max-height: 250px; }
          .stat-card-clickable { min-width: 100%; }
        }
      `}</style>
    </>
  );
};

export default GpsTrackingMap;